import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined'
import AutoAwesome from '@mui/icons-material/AutoAwesome'
import AutoMode from '@mui/icons-material/AutoMode'
import { Chip } from '@mui/material'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium'
import { useTranslation } from 'react-i18next'
import { useFormatter } from '@omnea-app/fe-i18n/useFormatter'
import { UserAvatar } from '@omnea-app/fe-users-and-groups/avatar/UserAvatar'
import { assertNever } from '@omnea-app/legacy/utils/type-guard/assertNever'
import { RequestTypeEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { RequestStepStatusChip } from './RequestStepStatusChip'
import type { GetSupplierStepsResponse } from '@omnea-app/requests/requestsFrontendClient'

type SupplierStepsRow = GetSupplierStepsResponse[number]
export function useGetSuppliersStepsColumns() {
  const { t } = useTranslation('supplier-dashboard', { keyPrefix: 'tasks-table' })
  const { formatDate } = useFormatter()

  const columns: GridColDef[] = [
    {
      field: 'blockName',
      headerName: t('columns.task-name'),
      flex: 1.5,
      minWidth: 160,
      hideable: false,
      sortable: true,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<SupplierStepsRow>) => <Typography>{params.row.blockName || '-'}</Typography>,
    },
    {
      field: 'name',
      headerName: t('columns.request-name'),
      flex: 1.5,
      minWidth: 160,
      hideable: false,
      sortable: true,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<SupplierStepsRow>) => <Typography>{params.row.requestName || '-'}</Typography>,
    },
    {
      field: 'type',
      headerName: t('columns.type'),
      flex: 1.5,
      minWidth: 140,
      sortable: true,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<SupplierStepsRow>) => <RequestTypeChip type={params.row.type} />,
    },
    {
      field: 'requestedBy',
      headerName: t('columns.requestedBy'),
      flex: 1.5,
      minWidth: 160,
      display: 'flex',
      sortable: true,
      valueGetter: (_, row) => row.requestor.name,
      renderCell: (params: GridRenderCellParams<SupplierStepsRow>) => (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <UserAvatar user={params.row.requestor} />
          <Typography>{params.row.requestor.name}</Typography>
        </Stack>
      ),
    },
    {
      field: 'requestedOn',
      sortable: true,
      flex: 1,
      minWidth: 120,
      display: 'flex',
      headerName: t('columns.requestedOn'),
      valueGetter: (_, row) => row.requestedOn && new Date(row.requestedOn),
      type: 'dateTime',
      renderCell: (params: GridRenderCellParams<SupplierStepsRow>) => (
        <Typography>{params.row.requestedOn ? formatDate(new Date(params.row.requestedOn), { format: 'short' }) : ''}</Typography>
      ),
    },
    {
      field: 'dueDate',
      sortable: true,
      flex: 1,
      minWidth: 120,
      display: 'flex',
      headerName: t('columns.dueDate'),
      valueGetter: (_, row) => row.dueDate && new Date(row.dueDate),
      type: 'dateTime',
      renderCell: (params: GridRenderCellParams<SupplierStepsRow>) => (
        <Typography>{params.row.dueDate ? formatDate(new Date(params.row.dueDate), { format: 'short' }) : ''}</Typography>
      ),
    },
    {
      field: 'status',
      headerName: t('columns.status'),
      flex: 1.5,
      minWidth: 160,
      sortable: true,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<SupplierStepsRow>) => (
        <RequestStepStatusChip
          stepType={params.row.stepType}
          tasks={params.row.tasks}
          status={params.row.status}
          amendments={params.row.amendmentRequests}
        />
      ),
    },
  ]
  return columns
}

const RequestTypeChip: React.FC<{ type: RequestTypeEnum }> = ({ type }) => {
  const { t } = useTranslation('supplier-dashboard', { keyPrefix: 'tasks-table.request-type' })

  switch (type) {
    case RequestTypeEnum.ASSESSMENT:
      return <Chip label={t(type)} color="primary" size="small" icon={<AssignmentOutlined />} />

    case RequestTypeEnum.NEW_PURCHASE:
    case RequestTypeEnum.ACCESS_REQUEST:
    case RequestTypeEnum.CATALOGUE_PURCHASE:
      return <Chip label={t(type)} color="primary" size="small" icon={<AutoAwesome />} />

    case RequestTypeEnum.RENEWAL:
      return <Chip label={t(type)} color="primary" size="small" icon={<AutoMode />} />
    default:
      assertNever(type)
      return null
  }
}
